import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  Building, Users, Target, BarChart3, FileText, Briefcase, 
  TrendingUp, Search, Filter, ChevronRight, Star, Mail, Phone,
  GraduationCap, Wrench, Shield, Rocket, CheckCircle, Clock,
  Eye, UserPlus, Award, Zap, ExternalLink, MapPin, DollarSign,
  Upload, FileSpreadsheet, BookOpen, Layers, ArrowUpRight, 
  AlertTriangle, RefreshCw, Download, ChevronUp, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier colors and labels for students
const STUDENT_TIER_CONFIG = {
  emerging: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Emerging', color: '#EF4444' },
  developing: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Developing', color: '#F59E0B' },
  internship_ready: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Internship Ready', color: '#3B82F6' },
  hire_ready: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Hire Ready', color: '#10B981' }
};

// Workforce tier colors
const WORKFORCE_TIER_CONFIG = {
  emerging: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Emerging', color: '#EF4444' },
  developing: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Developing', color: '#F59E0B' },
  promotion_ready: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Promotion Ready', color: '#3B82F6' },
  leadership_ready: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Leadership Ready', color: '#10B981' }
};

// Pathway icons
const PATHWAY_ICONS = {
  college: GraduationCap,
  trade: Wrench,
  military: Shield,
  entrepreneurship: Rocket,
  workforce: Briefcase
};

// KPI Card Component
const KPICard = ({ icon: Icon, label, value, subValue, trend, color = 'emerald' }) => (
  <GlassCard className="p-5">
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 bg-${color}-500/20 rounded-lg flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      {trend && (
        <span className={`text-xs px-2 py-0.5 rounded-full ${trend > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <p className="text-3xl font-bold text-white">{value}</p>
    <p className="text-sm text-slate-300">{label}</p>
    {subValue && <p className="text-xs text-slate-300 mt-1">{subValue}</p>}
  </GlassCard>
);

// Employee Row Component
const EmployeeRow = ({ employee, onView }) => {
  const tierConfig = WORKFORCE_TIER_CONFIG[employee.cri_tier] || WORKFORCE_TIER_CONFIG.emerging;
  
  return (
    <motion.tr 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors"
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
            {employee.full_name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div>
            <p className="text-white font-medium text-sm">{employee.full_name}</p>
            <p className="text-slate-300 text-xs">{employee.email}</p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-slate-300 text-sm">{employee.department || '-'}</td>
      <td className="py-3 px-4 text-slate-300 text-sm">{employee.job_title || '-'}</td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className={`h-full`}
              style={{ width: `${employee.cri_score || 0}%`, backgroundColor: tierConfig.color }}
            />
          </div>
          <span className={`text-sm font-medium ${tierConfig.text}`}>{employee.cri_score || 0}</span>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${tierConfig.bg} ${tierConfig.text}`}>
          {tierConfig.label}
        </span>
      </td>
      <td className="py-3 px-4">
        <Button size="sm" variant="ghost" onClick={() => onView(employee)} className="text-slate-300 hover:text-white">
          <Eye className="w-4 h-4" />
        </Button>
      </td>
    </motion.tr>
  );
};

// Candidate Card Component
const CandidateCard = ({ candidate, onConnect, onView }) => {
  const tierConfig = STUDENT_TIER_CONFIG[candidate.cri_tier] || STUDENT_TIER_CONFIG.emerging;
  const PathwayIcon = PATHWAY_ICONS[candidate.pathway] || Target;
  
  return (
    <GlassCard className="p-5 hover:border-emerald-500/30 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-white font-bold text-lg">
            {candidate.display_name?.charAt(0).toUpperCase() || '?'}
          </div>
          <div>
            <h3 className="text-white font-semibold">{candidate.display_name}</h3>
            <p className="text-slate-300 text-sm flex items-center gap-2">
              <PathwayIcon className="w-3 h-3" />
              {candidate.pathway?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${tierConfig.bg} ${tierConfig.text}`}>
          {tierConfig.label}
        </span>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-slate-300 text-sm">Career Readiness Index</span>
          <span className={`text-lg font-bold ${tierConfig.text}`}>{candidate.cri_score}</span>
        </div>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full"
            style={{ backgroundColor: tierConfig.color }}
            initial={{ width: 0 }}
            animate={{ width: `${candidate.cri_score}%` }}
            transition={{ duration: 0.8 }}
          />
        </div>
      </div>

      <div className="mb-4">
        <p className="text-slate-300 text-xs mb-2">Top Skills</p>
        <div className="flex flex-wrap gap-1">
          {candidate.top_skills?.slice(0, 4).map((skill, i) => (
            <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-slate-800 text-slate-300">
              {skill}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-slate-800">
        <div className="text-xs text-slate-300">
          From: <span className="text-slate-300">{candidate.source_org}</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onView(candidate)} className="border-slate-600 text-slate-300">
            <Eye className="w-3 h-3 mr-1" /> View
          </Button>
          {!candidate.is_connected && (
            <Button size="sm" onClick={() => onConnect(candidate)} className="bg-emerald-600 hover:bg-emerald-700">
              <UserPlus className="w-3 h-3 mr-1" /> Connect
            </Button>
          )}
        </div>
      </div>
    </GlassCard>
  );
};

// File Upload Component
const FileUploadArea = ({ onUpload, uploading, label, accept = ".csv" }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onUpload(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) onUpload(file);
  };

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
        dragOver ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 hover:border-slate-600'
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {uploading ? (
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
          <p className="text-slate-300">Uploading...</p>
        </div>
      ) : (
        <>
          <Upload className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <p className="text-white font-medium mb-2">{label}</p>
          <p className="text-slate-300 text-sm mb-4">Drag & drop or click to browse</p>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            className="border-slate-600"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Select CSV File
          </Button>
        </>
      )}
    </div>
  );
};

// Main Employer Dashboard
export default function EmployerDashboard() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('workforce');
  
  // Workforce Intelligence State
  const [workforceData, setWorkforceData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [learningRecords, setLearningRecords] = useState({ records: [], summary: {} });
  const [skillGaps, setSkillGaps] = useState({ skill_gaps: [], recommendations: [] });
  const [mobilitySignals, setMobilitySignals] = useState({ mobility_signals: [], insights: [] });
  
  // Talent Pipeline State
  const [candidates, setCandidates] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [jobs, setJobs] = useState([]);
  
  // UI State
  const [uploading, setUploading] = useState(false);
  const [calculatingCRI, setCalculatingCRI] = useState(false);
  const [filters, setFilters] = useState({
    minCri: 50,
    pathway: '',
    searchTerm: '',
    department: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchWorkforceData(),
        fetchEmployees(),
        fetchLearningRecords(),
        fetchTalentPipeline()
      ]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkforceData = async () => {
    try {
      const res = await axios.get(`${API}/workforce/dashboard`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setWorkforceData(res.data);
    } catch (error) {
      console.error('Failed to fetch workforce dashboard:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/workforce/employees`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setEmployees(res.data.employees || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchLearningRecords = async () => {
    try {
      const res = await axios.get(`${API}/workforce/lms/records`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setLearningRecords(res.data);
    } catch (error) {
      console.error('Failed to fetch learning records:', error);
    }
  };

  const fetchTalentPipeline = async () => {
    try {
      const [pipelineRes, partnershipsRes] = await Promise.all([
        axios.get(`${API}/enterprise/employer/pipeline?min_cri=${filters.minCri}`, {
          ...getAuthHeader(),
          withCredentials: true
        }),
        axios.get(`${API}/enterprise/partnerships`, {
          ...getAuthHeader(),
          withCredentials: true
        })
      ]);
      
      setCandidates(pipelineRes.data.candidates || []);
      setPartnerships(partnershipsRes.data.partnerships || []);
    } catch (error) {
      console.error('Failed to fetch pipeline:', error);
    }
  };

  const fetchSkillGaps = async () => {
    try {
      const res = await axios.get(`${API}/workforce/skill-gaps`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setSkillGaps(res.data);
    } catch (error) {
      console.error('Failed to fetch skill gaps:', error);
    }
  };

  const fetchMobilitySignals = async () => {
    try {
      const res = await axios.get(`${API}/workforce/mobility`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setMobilitySignals(res.data);
    } catch (error) {
      console.error('Failed to fetch mobility signals:', error);
    }
  };

  // Upload Handlers
  const handleEmployeeUpload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/workforce/employees/import`, formData, {
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      
      toast.success(`Imported ${res.data.imported} employees`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to import employees');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleLMSUpload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/workforce/lms/upload`, formData, {
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      
      toast.success(`Imported ${res.data.imported} learning records`);
      fetchLearningRecords();
    } catch (error) {
      toast.error('Failed to import LMS data');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleCalculateCRI = async () => {
    setCalculatingCRI(true);
    try {
      const res = await axios.post(`${API}/workforce/cri/calculate`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      
      toast.success(`Calculated CRI for ${res.data.calculated} employees`);
      await Promise.all([fetchWorkforceData(), fetchEmployees()]);
    } catch (error) {
      toast.error('Failed to calculate CRI scores');
      console.error(error);
    } finally {
      setCalculatingCRI(false);
    }
  };

  const handleConnect = (candidate) => {
    toast.success(`Connection request sent to ${candidate.display_name}`);
  };

  const handleViewCandidate = (candidate) => {
    toast.info(`Viewing profile for ${candidate.display_name}`);
  };

  const handleViewEmployee = (employee) => {
    toast.info(`Viewing ${employee.full_name}'s profile`);
  };

  // Filter employees
  const filteredEmployees = employees.filter(e => {
    if (filters.department && e.department !== filters.department) return false;
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      if (!e.full_name?.toLowerCase().includes(term) && !e.email?.toLowerCase().includes(term)) {
        return false;
      }
    }
    return true;
  });

  // Filter candidates
  const filteredCandidates = candidates.filter(c => {
    if (filters.pathway && c.pathway !== filters.pathway) return false;
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      if (!c.display_name?.toLowerCase().includes(term) && 
          !c.top_skills?.some(s => s.toLowerCase().includes(term))) {
        return false;
      }
    }
    return true;
  });

  // Get unique departments
  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];

  // Prepare chart data
  const tierChartData = workforceData?.tier_distribution ? 
    Object.entries(workforceData.tier_distribution).map(([key, value]) => ({
      name: WORKFORCE_TIER_CONFIG[key]?.label || key,
      value,
      color: WORKFORCE_TIER_CONFIG[key]?.color || '#888'
    })) : [];

  const deptChartData = workforceData?.department_readiness?.slice(0, 5).map(d => ({
    name: d.department?.length > 12 ? d.department.substring(0, 12) + '...' : d.department,
    cri: d.avg_cri,
    employees: d.employee_count
  })) || [];

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader 
        title="Workforce Intelligence"
        description="Internal talent analytics and external candidate pipeline"
      />

      {/* Role Badge */}
      <div className="flex items-center gap-4 mb-6">
        <span className="px-3 py-1 rounded-full text-sm bg-blue-500/20 text-blue-400 font-medium flex items-center gap-2">
          <Building className="w-4 h-4" />
          EMPLOYER
        </span>
        <span className="text-slate-300 text-sm">
          {workforceData?.kpis?.total_employees || 0} employees · {partnerships.length} partner{partnerships.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-800/50 p-1 mb-6">
          <TabsTrigger value="workforce" className="data-[state=active]:bg-blue-600" data-testid="tab-workforce">
            <Users className="w-4 h-4 mr-2" /> Internal Workforce
          </TabsTrigger>
          <TabsTrigger value="learning" className="data-[state=active]:bg-blue-600" data-testid="tab-learning">
            <BookOpen className="w-4 h-4 mr-2" /> Learning & Development
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="data-[state=active]:bg-emerald-600" data-testid="tab-pipeline">
            <Target className="w-4 h-4 mr-2" /> Talent Pipeline
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-purple-600" data-testid="tab-analytics">
            <BarChart3 className="w-4 h-4 mr-2" /> Analytics
          </TabsTrigger>
        </TabsList>

        {/* Internal Workforce Tab */}
        <TabsContent value="workforce">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KPICard 
              icon={Users} 
              label="Total Employees" 
              value={workforceData?.kpis?.total_employees || 0}
              subValue={`${workforceData?.kpis?.active_employees || 0} active`}
              color="blue"
            />
            <KPICard 
              icon={Target} 
              label="Avg CRI Score" 
              value={workforceData?.kpis?.avg_cri_score || 0}
              color="purple"
            />
            <KPICard 
              icon={TrendingUp} 
              label="Promotion Ready" 
              value={`${workforceData?.kpis?.promotion_ready_percent || 0}%`}
              color="emerald"
            />
            <KPICard 
              icon={BookOpen} 
              label="Learning Completion" 
              value={`${workforceData?.kpis?.learning_completion_rate || 0}%`}
              subValue={`${workforceData?.kpis?.completed_courses || 0} courses`}
              color="amber"
            />
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-3 mb-6">
            <Button 
              onClick={handleCalculateCRI} 
              disabled={calculatingCRI}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="btn-calculate-cri"
            >
              {calculatingCRI ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Recalculate CRI
            </Button>
            <Button variant="outline" className="border-slate-600" onClick={() => fetchSkillGaps()}>
              <AlertTriangle className="w-4 h-4 mr-2" /> Skill Gap Analysis
            </Button>
            <Button variant="outline" className="border-slate-600" onClick={() => fetchMobilitySignals()}>
              <ArrowUpRight className="w-4 h-4 mr-2" /> Mobility Signals
            </Button>
          </div>

          {/* Employees Table or Upload */}
          {employees.length > 0 ? (
            <GlassCard className="p-0 overflow-hidden">
              <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Employee Roster</h3>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <Input
                      placeholder="Search employees..."
                      value={filters.searchTerm}
                      onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                      className="pl-9 w-64 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <select
                    value={filters.department}
                    onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                    className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
                  >
                    <option value="">All Departments</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr className="text-left text-slate-300 text-sm">
                      <th className="py-3 px-4 font-medium">Employee</th>
                      <th className="py-3 px-4 font-medium">Department</th>
                      <th className="py-3 px-4 font-medium">Title</th>
                      <th className="py-3 px-4 font-medium">CRI Score</th>
                      <th className="py-3 px-4 font-medium">Tier</th>
                      <th className="py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.slice(0, 20).map(employee => (
                      <EmployeeRow key={employee.id} employee={employee} onView={handleViewEmployee} />
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredEmployees.length > 20 && (
                <div className="p-4 text-center border-t border-slate-800">
                  <p className="text-slate-300 text-sm">Showing 20 of {filteredEmployees.length} employees</p>
                </div>
              )}
            </GlassCard>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  Import Employees
                </h3>
                <p className="text-slate-300 text-sm mb-4">
                  Upload a CSV file with employee data. Required columns: email, full_name. 
                  Optional: department, job_title, location, skills.
                </p>
                <FileUploadArea 
                  onUpload={handleEmployeeUpload}
                  uploading={uploading}
                  label="Upload Employee Roster"
                />
              </GlassCard>
              
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
                  CSV Format Example
                </h3>
                <div className="bg-slate-900 rounded-lg p-4 font-mono text-xs text-slate-300 overflow-x-auto">
                  <pre>{`email,full_name,department,job_title,skills
john@company.com,John Doe,Engineering,Senior Developer,"Python,React"
jane@company.com,Jane Smith,Marketing,Manager,"SEO,Content"
bob@company.com,Bob Johnson,Sales,Account Executive,"CRM,Negotiation"`}</pre>
                </div>
              </GlassCard>
            </div>
          )}
        </TabsContent>

        {/* Learning & Development Tab */}
        <TabsContent value="learning">
          {/* Learning KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KPICard 
              icon={BookOpen} 
              label="Total Courses" 
              value={learningRecords.summary?.total || 0}
              color="blue"
            />
            <KPICard 
              icon={CheckCircle} 
              label="Completed" 
              value={learningRecords.summary?.completed || 0}
              color="emerald"
            />
            <KPICard 
              icon={Clock} 
              label="In Progress" 
              value={learningRecords.summary?.in_progress || 0}
              color="amber"
            />
            <KPICard 
              icon={Award} 
              label="Certifications" 
              value={learningRecords.summary?.certifications || 0}
              color="purple"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Upload LMS Data */}
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-emerald-400" />
                Import LMS Data
              </h3>
              <p className="text-slate-300 text-sm mb-4">
                Upload training data from your LMS. Required columns: employee_email, course_name, completion_status.
              </p>
              <FileUploadArea 
                onUpload={handleLMSUpload}
                uploading={uploading}
                label="Upload LMS Export"
              />
              
              <div className="mt-4 bg-slate-900 rounded-lg p-4 font-mono text-xs text-slate-300 overflow-x-auto">
                <p className="text-slate-300 mb-2 font-sans text-sm">Example CSV format:</p>
                <pre>{`employee_email,course_name,completion_status,skill_tag
john@co.com,Python Fundamentals,completed,Python
jane@co.com,Leadership 101,in_progress,Leadership`}</pre>
              </div>
            </GlassCard>

            {/* Learning Records Summary */}
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-400" />
                Learning Overview
              </h3>
              
              {learningRecords.records?.length > 0 ? (
                <div className="space-y-4">
                  {/* Completion Rate */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-300 text-sm">Overall Completion Rate</span>
                      <span className="text-emerald-400 font-semibold">{learningRecords.summary?.completion_rate || 0}%</span>
                    </div>
                    <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${learningRecords.summary?.completion_rate || 0}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Recent Courses */}
                  <div>
                    <p className="text-slate-300 text-sm mb-3">Recent Courses</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {learningRecords.records.slice(0, 5).map((record, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800">
                          <div>
                            <p className="text-white text-sm">{record.course_name}</p>
                            <p className="text-slate-300 text-xs">{record.employee_email}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            record.completion_status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                            record.completion_status === 'in_progress' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-slate-700 text-slate-300'
                          }`}>
                            {record.completion_status?.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-300">No learning records yet</p>
                  <p className="text-slate-300 text-sm">Upload LMS data to track training progress</p>
                </div>
              )}
            </GlassCard>
          </div>
        </TabsContent>

        {/* Talent Pipeline Tab */}
        <TabsContent value="pipeline">
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
              <Input
                placeholder="Search by name or skill..."
                value={filters.searchTerm}
                onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                className="pl-9 bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <select
              value={filters.pathway}
              onChange={(e) => setFilters(prev => ({ ...prev, pathway: e.target.value }))}
              className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white"
            >
              <option value="">All Pathways</option>
              <option value="college">College</option>
              <option value="trade">Trade</option>
              <option value="entrepreneurship">Entrepreneurship</option>
              <option value="workforce">Workforce</option>
              <option value="military">Military</option>
            </select>
            <select
              value={filters.minCri}
              onChange={(e) => setFilters(prev => ({ ...prev, minCri: parseInt(e.target.value) }))}
              className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white"
            >
              <option value="0">Any CRI</option>
              <option value="50">CRI 50+</option>
              <option value="60">CRI 60+</option>
              <option value="70">CRI 70+</option>
              <option value="80">CRI 80+</option>
            </select>
          </div>

          {/* Pipeline Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KPICard 
              icon={Users} 
              label="Available Candidates" 
              value={candidates.length}
              color="emerald"
            />
            <KPICard 
              icon={Award} 
              label="Hire Ready" 
              value={candidates.filter(c => c.cri_tier === 'hire_ready').length}
              color="blue"
            />
            <KPICard 
              icon={Target} 
              label="Avg CRI Score" 
              value={candidates.length > 0 ? Math.round(candidates.reduce((acc, c) => acc + c.cri_score, 0) / candidates.length) : 0}
              color="purple"
            />
            <KPICard 
              icon={Building} 
              label="Partner Schools" 
              value={partnerships.length}
              color="amber"
            />
          </div>

          {/* Candidates Grid */}
          {filteredCandidates.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCandidates.map(candidate => (
                <CandidateCard
                  key={candidate.id}
                  candidate={candidate}
                  onConnect={handleConnect}
                  onView={handleViewCandidate}
                />
              ))}
            </div>
          ) : (
            <GlassCard className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">No Candidates Found</h3>
              <p className="text-slate-300">
                {candidates.length === 0 
                  ? "No candidates have consented to employer visibility yet. Partner with schools to access qualified talent."
                  : "Try adjusting your filters to see more candidates."}
              </p>
              {candidates.length === 0 && (
                <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                  Request Partnership
                </Button>
              )}
            </GlassCard>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Readiness Distribution Pie Chart */}
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-400" />
                Workforce Readiness Distribution
              </h3>
              {tierChartData.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={tierChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {tierChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ background: '#1e293b', border: 'none', borderRadius: '8px' }}
                      itemStyle={{ color: '#fff' }}
                    />
                    <Legend 
                      verticalAlign="bottom"
                      formatter={(value) => <span className="text-slate-300 text-sm">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <p className="text-slate-300">No CRI data available yet</p>
                </div>
              )}
            </GlassCard>

            {/* Department CRI Bar Chart */}
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                Department Readiness
              </h3>
              {deptChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={deptChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis type="number" domain={[0, 100]} stroke="#C6D4E8" />
                    <YAxis dataKey="name" type="category" width={100} stroke="#C6D4E8" />
                    <Tooltip 
                      contentStyle={{ background: '#1e293b', border: 'none', borderRadius: '8px' }}
                      itemStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="cri" fill="#3B82F6" radius={[0, 4, 4, 0]} name="Avg CRI" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <p className="text-slate-300">No department data available</p>
                </div>
              )}
            </GlassCard>

            {/* Skill Gaps */}
            <GlassCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Skill Gaps Analysis
                </h3>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-slate-600"
                  onClick={fetchSkillGaps}
                >
                  <RefreshCw className="w-3 h-3 mr-1" /> Analyze
                </Button>
              </div>
              
              {skillGaps.skill_gaps?.length > 0 ? (
                <div className="space-y-3">
                  {skillGaps.skill_gaps.slice(0, 6).map((gap, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full ${gap.gap_severity === 'high' ? 'bg-red-500' : 'bg-amber-500'}`} />
                        <span className="text-white text-sm">{gap.skill}</span>
                      </div>
                      <span className="text-slate-300 text-sm">{gap.current_coverage}% coverage</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-300 text-sm">Click Analyze to identify skill gaps</p>
                </div>
              )}
            </GlassCard>

            {/* Internal Mobility */}
            <GlassCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <ArrowUpRight className="w-5 h-5 text-emerald-400" />
                  High Potential Employees
                </h3>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="border-slate-600"
                  onClick={fetchMobilitySignals}
                >
                  <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                </Button>
              </div>
              
              {mobilitySignals.mobility_signals?.length > 0 ? (
                <div className="space-y-3">
                  {mobilitySignals.mobility_signals.slice(0, 5).map((signal, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                      <div>
                        <p className="text-white text-sm font-medium">{signal.employee_name}</p>
                        <p className="text-slate-300 text-xs">{signal.department}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          signal.cri_score >= 80 ? 'text-emerald-400' : 
                          signal.cri_score >= 60 ? 'text-blue-400' : 'text-amber-400'
                        }`}>
                          {signal.cri_score}
                        </span>
                        {signal.leadership_potential && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-400">
                            Leadership
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-300 text-sm">Click Refresh to find high potential employees</p>
                </div>
              )}
            </GlassCard>
          </div>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
